<template>
	<div class="campaign-info g-1">
		<h2
			class="campaign-info__title text-high m-0"
			:class="[`campaign-info__title--${getTitleSize}`, isHighContrast ? 'text-medium-upsidedown' : 'text-high']"
			data-testid="sr__title"
		>
			{{ promotionalTitle }}
		</h2>

		<div v-if="!hidePromotionalText && !hasDisclaimerText" class="campaign-info__description">
			<div v-if="promotionalText" class="campaign-info__promotionalText text-high" data-testid="sr__promo-text">
				{{ promotionalText }}
			</div>

			<TitleScoreList
				v-if="!sponsoredCampaign?.hideRatings && campaignNode.content.scoring"
				:imdbOnly="true"
				:lazy="lazy"
				:scores="campaignNode.content.scoring"
				:externalIds="campaignNode.content.externalIds"
				data-testid="sr__rating"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { useCampaignInfo } from '@/features/sponsored-recommendations/composables/useSendBidId';

interface CampaignInfoProps {
	sponsoredCampaign: NonNullable<SponsoredAdFragment['campaign']>;
	lazy: boolean;
	hidePromotionalText?: boolean;
	hideRating?: boolean;
	isTitleSizeFixed?: boolean;
}

const props = withDefaults(defineProps<CampaignInfoProps>(), {
	lazy: true,
	hidePromotionalText: false,
	hideRating: false,
	isTitleSizeFixed: false,
});

const TitleScoreList = defineAsyncComponent(() => import('@/components/TitleScoreList.vue'));

const { campaignNode, promotionalTitle, promotionalText, hasDisclaimerText } = useCampaignInfo(
	() => props.sponsoredCampaign
);

const getTitleSize = computed<'regular' | 'medium' | 'small'>(() => {
	if (props.isTitleSizeFixed) {
		return 'regular';
	}

	const titleLength = promotionalTitle.value?.length ?? 0;

	if (titleLength >= 22 && titleLength < 32) return 'medium';

	if (titleLength >= 32) return 'small';

	return 'regular';
});

// Hacks & Exceptions
/** Campaigns with a light colored background that need darker text. */
const highContrastCampaigns = ['TWIXMAS_BURST2_GB_SRA'];
const isHighContrast = computed(() => highContrastCampaigns.includes(props.sponsoredCampaign.name));
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.campaign-info {
	display: grid;

	:deep(.jw-scoring-listing__rating) {
		color: $jw-color-lightgray--dark;
		font-weight: 600;

		display: flex;
		margin-inline-start: var(--jw-spacing-1);
		gap: var(--jw-spacing-1);

		&::before {
			margin: 0 var(--jw-spacing-1);
			content: ' | ';
		}
	}

	&__disclaimer {
		font-size: 12px;
		padding-block-start: var(--jw-spacing-1);
	}

	&__promotionalText {
		font-size: 14px;
		font-weight: 400;
	}

	&__description {
		display: flex;
		align-items: baseline;
	}

	&__title {
		font-weight: bold;
		font-size: 25px;
		white-space: pre-line;

		&--regular {
			font-size: 25px;
		}

		&--medium {
			font-size: 18px;
		}

		&--small {
			font-size: 16px;
		}

		@container sponsoredRecommendation (min-width: 450px) {
			max-width: 80%;
		}
	}

	&__button {
		all: unset;
		color: var(--jw-shade-white);
	}

	@container srCompact (max-width: 360px) {
		&__title {
			&--regular {
				font-size: 20px;
			}

			&--medium {
				font-size: 15px;
			}

			&--small {
				font-size: 14px;
			}
		}

		&__promotionalText {
			font-size: 12px;
		}

		&__disclaimer {
			font-size: 10px;
		}
	}
}
</style>
